/**
 * EVENTSFLO-SHIFT-RULE-STATUS-SHIELD-NON-BLOCKING-V1
 *
 * Compact, advisory rule-status badge for a single shift.
 *
 * Visual language (no alarm-red — this is guidance, not enforcement):
 *   • Rules aligned     → green check
 *   • Outside rules     → amber shield
 *   • Manual override   → blue shield
 *
 * Hover/focus the badge to read a plain-language explanation of every
 * category contributing to the overall status. Clicking the badge
 * fires the optional `onReview` callback so the parent can open the
 * shift's edit modal as a "Review rules" affordance — there is NO
 * "Reapply rules" action wired here.
 */
import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Shield, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { getShiftRuleStatus } from "../../lib/api";

const STATUS_CONFIG = {
  aligned: {
    label: "Rules aligned",
    Icon: ShieldCheck,
    chipClasses:
      "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/20",
    dotClasses: "bg-emerald-500",
  },
  outside_rules: {
    label: "Outside rules setting",
    Icon: ShieldAlert,
    chipClasses:
      "bg-amber-500/10 text-amber-700 border-amber-500/30 hover:bg-amber-500/20",
    dotClasses: "bg-amber-500",
  },
  manual_override: {
    label: "Manual override",
    Icon: Shield,
    chipClasses:
      "bg-sky-500/10 text-sky-700 border-sky-500/30 hover:bg-sky-500/20",
    dotClasses: "bg-sky-500",
  },
};

export const ShiftRuleStatusBadge = ({
  shiftId,
  compact = true,
  onReview,
  onLoaded,
  refreshKey,
  className = "",
}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shiftId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getShiftRuleStatus(shiftId);
        if (!cancelled) {
          setStatus(res.data);
          // EVENTSFLO-SHIFT-RULE-SUPPRESSED-ITEM-LABEL-VISIBILITY-FIX-V1
          // Surface the resolved rule-status to the parent so it can
          // render label-level badges next to the Meal / Crew Chief
          // rule-label selectors without issuing a duplicate fetch.
          if (typeof onLoaded === "function") {
            try { onLoaded(res.data); } catch { /* swallow consumer errors */ }
          }
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [shiftId, refreshKey, onLoaded]);

  if (loading) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs text-muted-foreground ${className}`}
        data-testid={`shift-rule-status-loading-${shiftId}`}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
      </span>
    );
  }

  if (error || !status) return null;

  const cfg = STATUS_CONFIG[status.overall] || STATUS_CONFIG.aligned;
  const { Icon, label, chipClasses } = cfg;
  const issues = (status.categories || []).filter(
    (c) => c.type !== "aligned" && c.type !== "manual_item"
  );
  const manualItems = (status.categories || []).filter((c) => c.type === "manual_item");

  const interactive = typeof onReview === "function";
  const ChipTag = interactive ? "button" : "span";

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <ChipTag
            type={interactive ? "button" : undefined}
            onClick={interactive ? () => onReview(status) : undefined}
            data-testid={`shift-rule-status-badge-${shiftId}`}
            data-status={status.overall}
            className={[
              "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5",
              "text-xs font-medium transition-colors",
              chipClasses,
              interactive ? "cursor-pointer" : "cursor-default",
              className,
            ].join(" ")}
          >
            <Icon className="h-3 w-3" />
            {!compact && <span>{label}</span>}
          </ChipTag>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-xs text-xs bg-popover text-popover-foreground border border-border"
          data-testid={`shift-rule-status-tooltip-${shiftId}`}
        >
          <div className="font-semibold mb-1">{label}</div>
          {status.overall === "aligned" && issues.length === 0 && (
            <div className="text-muted-foreground">
              All deterministic rules match the current shift.
            </div>
          )}
          {issues.length > 0 && (
            <ul className="space-y-1">
              {issues.map((c, i) => (
                <li key={i}>
                  <div className="font-medium">{c.label}</div>
                  {c.detail && (
                    <div className="text-muted-foreground">{c.detail}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {manualItems.length > 0 && (
            <div className="mt-2 pt-2 border-t border-border/40">
              <div className="font-medium mb-0.5">Manual supplements</div>
              <ul className="text-muted-foreground space-y-0.5">
                {manualItems.map((c, i) => (
                  <li key={i}>{c.detail || c.label}</li>
                ))}
              </ul>
            </div>
          )}
          {interactive && (
            <div className="mt-2 pt-2 border-t border-border/40 text-muted-foreground">
              Click to review the shift.
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ShiftRuleStatusBadge;
